/**
 * Dark Reader
 *
 * The first extension here detected without probing a web-accessible resource. Dark
 * Reader exposes none that are reachable — but it doesn't need to expose anything,
 * because its whole job is rewriting the page, and it labels its own work.
 *
 * Three independent markers, all in a `darkreader` namespace, so this identifies Dark
 * Reader specifically rather than "some dark-mode extension":
 *
 *   <html data-darkreader-mode="dynamic" data-darkreader-scheme="dark">
 *   <style class="darkreader darkreader--sync">          (9 such <style> elements)
 *   <div data-darkreader-inline-bgcolor>                 (on elements with inline colour)
 *
 * Observed on version 4.9.129 via `tooling/probe-interactions.js`.
 *
 * Caveat this shares with all behavioural signals: it only fires when the extension is
 * actually *doing* something. Dark Reader disabled, or in light mode, or with the
 * current site allowlisted, leaves no trace. A negative is therefore much weaker
 * evidence than a negative from a resource probe.
 */

import { probeRootAttribute, probeSelector, probeBait, baitAttributesWithPrefix } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'eimadpbcbfnmbkopoojfekhnkhdbieeh';

export const darkReader: ExtensionDefinition = {
  id: ID,
  name: 'Dark Reader',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 6_000_000,
  notes:
    'Detected behaviourally, not by resource probing. Only fires while Dark Reader is ' +
    'actively theming the page — if it is off or the site is allowlisted, nothing shows.',

  signals: [
    {
      id: 'dom-root-mode-attribute',
      kind: 'dom',
      description:
        'Check for <html data-darkreader-mode>. Dark Reader stamps the document root so ' +
        'it can recognise its own work; the attribute is vendor-namespaced.',
      weight: 1,
      run: () => probeRootAttribute('data-darkreader-mode'),
    },
    {
      id: 'dom-injected-stylesheet',
      kind: 'dom',
      description:
        'Look for a <style class="darkreader--sync"> element. Dark Reader injects nine ' +
        'such stylesheets to override page colours.',
      weight: 1,
      run: () => probeSelector('style.darkreader, style[class*="darkreader--"]', { timeoutMs: 1200 }),
    },
    {
      id: 'dom-bait-inline-rewrite',
      kind: 'dom',
      description:
        'Insert a bait element with an inline background colour and see whether Dark ' +
        'Reader rewrites it — it records the original in data-darkreader-inline-* ' +
        'attributes so it can restore them later.',
      weight: 1,
      run: () =>
        probeBait(
          '<div style="background:#ffffff;color:#000000;width:200px;height:60px"></div>',
          (el) => {
            const marks = baitAttributesWithPrefix(el, 'data-darkreader-inline');
            return {
              fired: marks.length > 0,
              detail: marks.length
                ? `bait element gained ${marks.join(', ')}`
                : 'bait element was not restyled',
            };
          },
          { waitMs: 2000 },
        ),
    },
  ],
};
